import { useCallback, useEffect, useRef, useState } from "react";
import { useProfiles } from "../contexts/ProfileContext";

// ---------------------------------------------------------------------------
// Badge labels
// ---------------------------------------------------------------------------

const PROFILE_BADGES: Record<string, string> = {
  internal: "Admin",
  student: "Student",
  counselor: "Counselor",
};

function getBadgeLabel(
  profileType: "internal" | "external",
  rolePreference?: string | null
): string {
  if (profileType === "internal") return PROFILE_BADGES["internal"] ?? "Admin";
  return PROFILE_BADGES[rolePreference ?? ""] ?? "External";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProfileSwitcher(): React.ReactElement | null {
  const { profiles, activeProfile, hasMultipleProfiles, switchProfile, setDefaultProfile } =
    useProfiles();

  const [open, setOpen] = useState(false);
  const [defaultLoading, setDefaultLoading] = useState(false);
  const [defaultError, setDefaultError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside — hooks must be called before any
  // conditional return to satisfy the Rules of Hooks.
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, handleClickOutside]);

  // Don't render if there's nothing to switch between
  if (!hasMultipleProfiles || !activeProfile) return null;

  const inactiveProfiles = profiles.filter((p) => p.profileType !== activeProfile.profileType);

  const handleSwitch = (profileType: "internal" | "external"): void => {
    switchProfile(profileType);
    setOpen(false);
  };

  const handleSetDefault = async (profileType: "internal" | "external"): Promise<void> => {
    setDefaultLoading(true);
    setDefaultError(null);
    try {
      await setDefaultProfile(profileType);
    } catch (err) {
      setDefaultError(err instanceof Error ? err.message : "Unable to set default");
    } finally {
      setDefaultLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === "Escape") {
      setOpen(false);
    }
  };

  const activeBadge = getBadgeLabel(activeProfile.profileType, activeProfile.rolePreference);

  return (
    <div ref={dropdownRef} className="relative" onKeyDown={handleKeyDown}>
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
        }}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={`Active profile: ${activeBadge}. Click to switch profiles.`}
        className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
      >
        <span
          className={`inline-block h-2 w-2 rounded-full ${
            activeProfile.profileType === "internal" ? "bg-amber-500" : "bg-green-500"
          }`}
        />
        <span>{activeProfile.displayName ?? activeProfile.email}</span>
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold text-gray-600">
          {activeBadge}
        </span>
        <svg
          className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Profile options"
          className="absolute right-0 z-10 mt-1 w-64 rounded-md border border-gray-200 bg-white shadow-lg"
        >
          {/* Current profile */}
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="text-xs font-medium uppercase text-gray-400">Active profile</p>
            <p className="mt-1 text-sm font-semibold text-gray-900">
              {activeProfile.displayName ?? activeProfile.email}
            </p>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-gray-500">{activeBadge}</span>
              {activeProfile.isDefault ? (
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                  Default
                </span>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  disabled={defaultLoading}
                  onClick={() => {
                    void handleSetDefault(activeProfile.profileType);
                  }}
                  className="text-xs text-gray-700 underline hover:text-gray-900 disabled:opacity-50"
                >
                  {defaultLoading ? "Setting..." : "Set as default"}
                </button>
              )}
            </div>
          </div>

          {/* Switch options */}
          {inactiveProfiles.map((profile) => {
            const badge = getBadgeLabel(profile.profileType, profile.rolePreference);
            return (
              <div key={profile.profileId} className="px-4 py-3">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    handleSwitch(profile.profileType);
                  }}
                  className="w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
                >
                  <p className="text-sm font-medium text-gray-900">Switch to {badge}</p>
                  <p className="text-xs text-gray-500">{profile.displayName ?? profile.email}</p>
                </button>
                {!profile.isDefault ? (
                  <button
                    type="button"
                    role="menuitem"
                    disabled={defaultLoading}
                    onClick={() => {
                      void handleSetDefault(profile.profileType);
                    }}
                    className="mt-1 text-xs text-gray-700 underline hover:text-gray-900 disabled:opacity-50"
                  >
                    {defaultLoading ? "Setting..." : "Set as default"}
                  </button>
                ) : (
                  <span className="mt-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                    Default
                  </span>
                )}
              </div>
            );
          })}

          {defaultError ? (
            <div className="border-t border-gray-100 px-4 py-2">
              <p className="text-xs text-red-600">{defaultError}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
